// svgUtils.js — helpers for the tokenised-SVG template editor.
// Used by both the certificate (CreateCertificate) and the student ID card.
// No external deps — works on Node 16 / CRA.

// The data tokens a template may contain, e.g. {{name}}, {{course}} ...
export const escapeXml = (str) =>
  String(str == null ? "" : str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Replace every {{token}} in the template with the matching value from `data`.
// Unknown tokens are left as-is so the admin can see what's missing.
export const substituteTokens = (template, data = {}) => {
  if (!template) return "";
  let out = template;
  Object.keys(data).forEach((key) => {
    const value = escapeXml(data[key]);
    out = out.split(`{{${key}}}`).join(value);
  });
  return out;
};

// Reverse of substituteTokens: turn a rendered SVG back into a reusable
// template by swapping known values back to their {{token}}. Longest values
// first so a short value can't partially match inside a longer one.
export const reverseTokens = (svg, data = {}) => {
  if (!svg) return "";
  let out = svg;
  const entries = Object.keys(data)
    .map((key) => ({ key, value: escapeXml(data[key]) }))
    .filter((e) => e.value && e.value.trim().length >= 2)
    .sort((a, b) => b.value.length - a.value.length);
  entries.forEach(({ key, value }) => {
    out = out.split(value).join(`{{${key}}}`);
  });
  return out;
};

// Parse "0 0 W H" from a viewBox (falls back to width/height attrs, then 1000x700).
export const getViewBox = (svg) => {
  if (svg) {
    const vb = svg.match(/viewBox\s*=\s*["']\s*([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)/i);
    if (vb) return { width: parseFloat(vb[3]), height: parseFloat(vb[4]) };
    const w = svg.match(/\bwidth\s*=\s*["']([\d.]+)/i);
    const h = svg.match(/\bheight\s*=\s*["']([\d.]+)/i);
    if (w && h) return { width: parseFloat(w[1]), height: parseFloat(h[1]) };
  }
  return { width: 1000, height: 700 };
};

// Ensure the root <svg> carries explicit width/height (some browsers render a
// viewBox-only SVG at 0x0 when loaded into an <img> for canvas export).
const withExplicitSize = (svg, width, height) => {
  if (/\bwidth\s*=/.test(svg.slice(0, svg.indexOf(">") + 1))) return svg;
  return svg.replace(/<svg/i, `<svg width="${width}" height="${height}"`);
};

// Rasterise an SVG string to a PNG Blob at `scale`x the viewBox size.
// NOTE: any <image> inside the SVG must be a data: URI or CORS-enabled,
// otherwise the canvas is tainted and export fails (we reject in that case).
export const svgToPngBlob = (svg, width, height, scale = 3) =>
  new Promise((resolve, reject) => {
    try {
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);

      const sized = withExplicitSize(svg, width, height);
      const blob = new Blob([sized], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
            "image/png"
          );
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("SVG failed to load (check for non-embedded images)"));
      };
      img.src = url;
    } catch (err) {
      reject(err);
    }
  });

// Trigger a browser download of a Blob.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
